import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Terminal from '../components/Terminal';
import { WorkerParticleCanvas } from '../components/WorkerParticleCanvas';
import usePerformanceOptimization from '../hooks/usePerformanceOptimization';
// Import the optimized styles
import '../styles/optimized-landing.css';
// Keep existing modular styles for complex effects
import '../styles/landing/variables.css';
import '../styles/landing/base.css';
import '../styles/landing/depth-3d.css';
import '../styles/landing/matrix-effects.css';
import '../styles/landing/navigation.css';
import '../styles/landing/particles.css';
import '../styles/landing/terminal.css';
import '../styles/landing/animations.css';
import '../styles/PageTransition.css';

const MatrixLanding: React.FC = () => {
  const landingRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [terminalCollapsed, setTerminalCollapsed] = useState(true);
  const [showSummonButton, setShowSummonButton] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionActive, setTransitionActive] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Use the performance optimization hook
  const {
    performanceMode,
    metrics,
    getOptimizedConfig,
    throttleCallback,
    debounceCallback,
    isFeatureEnabled,
    cleanupResources
  } = usePerformanceOptimization();

  // Get optimized configuration based on performance
  const config = getOptimizedConfig();

  // Calculate particle count based on existing config logic
  const particleCount = useMemo(() => {
    if (!isFeatureEnabled('particles')) return 0;
    return Math.floor(config.particleCount * 0.6); // Keep the 40% reduction from original
  }, [config.particleCount, isFeatureEnabled]);

  // Particle colors from original system
  const particleColors = useMemo(() => ['#00ff41', '#0066ff', '#00d4ff'], []);

  // Performance change handler for particle system
  const handleParticlePerformanceChange = useCallback((mode: string, metrics: any) => {
    console.log(`[Landing] Particle performance mode changed to: ${mode}`, metrics);
  }, []);

  // Navigation items for floating cards
  const navCards = useMemo(() => [
    { id: 'projects', label: 'Projects', href: '/projects', angle: 45 },
    { id: 'store', label: 'Store', href: '/store', angle: 135 },
    { id: 'articles', label: 'Articles', href: '/articles', angle: 225 },
    { id: 'about', label: 'About', href: '/about', angle: 315 },
  ], []);

  // Initialize component state on mount
  useEffect(() => {
    // Reset any lingering transition states
    setIsTransitioning(false);
    setTransitionActive(false);
    
    // Remove any transition classes that might be lingering
    if (landingRef.current) {
      landingRef.current.classList.remove('cyber-landing--transitioning');
    }
  }, []);

  // Prevent transitions during viewport resize (dev mode fix)
  useEffect(() => {
    let resizeTimer: NodeJS.Timeout;

    const handleResize = () => {
      // Add resizing class to disable transitions
      document.body.classList.add('resizing');
      
      // Clear existing timer
      clearTimeout(resizeTimer);
      
      // Remove resizing class after resize is complete
      resizeTimer = setTimeout(() => {
        document.body.classList.remove('resizing');
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Optimized visibility detection
  useEffect(() => {
    // Set visible immediately on mount to prevent unwanted transitions
    setIsVisible(true);
    
    // Add loaded class immediately to prevent opacity transition issues
    if (landingRef.current) {
      landingRef.current.classList.add('cyber-landing--loaded');
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('cyber-landing--loaded');
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (landingRef.current) {
      observer.observe(landingRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Show summon button with optimized timing
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSummonButton(true);
    }, performanceMode === 'low' ? 1000 : 2000);

    return () => clearTimeout(timer);
  }, [performanceMode]);

  // Optimized mouse tracking
  const handleMouseMove = throttleCallback((e: MouseEvent) => {
    if (performanceMode === 'low' || !isFeatureEnabled('parallax')) return;
    
    setMousePosition({
      x: (e.clientX / window.innerWidth) * 100,
      y: (e.clientY / window.innerHeight) * 100,
    });
  }, performanceMode === 'high' ? 16 : 32);

  useEffect(() => {
    if (isFeatureEnabled('parallax')) {
      window.addEventListener('mousemove', handleMouseMove);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove, isFeatureEnabled]);

  const triggerPageExplosion = useCallback((targetPath: string) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setTransitionActive(true);

    if (landingRef.current) {
      landingRef.current.classList.add('cyber-landing--transitioning');
    }

    // Use simpler transition for low performance
    const transitionTime = performanceMode === 'low' ? 1000 : 3000;
    setTimeout(() => {
      navigate(targetPath);
    }, transitionTime);
  }, [isTransitioning, navigate, performanceMode]);

  // Reset transition state after navigation
  useEffect(() => {
    if (isTransitioning) {
      const resetTimer = setTimeout(() => {
        setIsTransitioning(false);
        setTransitionActive(false);
        cleanupResources(); // Clean up resources during transition
        if (landingRef.current) {
          landingRef.current.classList.remove('cyber-landing--transitioning');
        }
      }, performanceMode === 'low' ? 2000 : 4000);

      return () => clearTimeout(resetTimer);
    }
  }, [isTransitioning, performanceMode, cleanupResources]);

  const handleNavigation = useCallback((path: string) => {
    triggerPageExplosion(path);
  }, [triggerPageExplosion]);

  const handleTitleClick = useCallback(() => {
    // Epic title portal activation with performance awareness
    if (isFeatureEnabled('glow')) {
      // Add special effects only if performance allows
    }
  }, [isFeatureEnabled]);

  const toggleTerminal = useCallback(() => {
    setTerminalCollapsed(!terminalCollapsed);
  }, [terminalCollapsed]);

  // Memoized data packets with performance scaling
  const dataPackets = useMemo(() => {
    if (performanceMode === 'low') return [];
    
    const count = performanceMode === 'high' ? 2 : 1; // Reduced count
    return Array.from({ length: count }).map((_, i) => ({ id: i }));
  }, [performanceMode]);

  return (
    <main 
      className={`
        cyber-landing gpu-optimized contain-strict mobile-optimized
        ${isTransitioning ? 'cyber-landing--transitioning' : ''}
        performance-${performanceMode}
      `} 
      ref={landingRef}
      data-performance={performanceMode}
      style={{
        contain: 'strict',
        willChange: isTransitioning ? 'transform, filter' : 'auto'
      }}
    >
      {/* Transition Overlay - Conditional based on performance */}
      {transitionActive && (
        <div className="page-transition page-transition--active contain-strict" data-performance={performanceMode}>
          <div className="page-transition__explosion-overlay"></div>
          {performanceMode !== 'low' && (
            <>
              <div className="page-transition__shockwave page-transition__shockwave--1"></div>
              <div className="page-transition__shockwave page-transition__shockwave--2"></div>
              <div className="page-transition__shockwave page-transition__shockwave--3"></div>
              {performanceMode === 'high' && (
                <>
                  <div className="page-transition__shockwave page-transition__shockwave--4"></div>
                  <div className="page-transition__shockwave page-transition__shockwave--5"></div>
                  <div className="page-transition__shockwave page-transition__shockwave--6"></div>
                </>
              )}
              
              {/* Energy Ripples - Performance aware */}
              <div className="page-transition__energy-ripple page-transition__energy-ripple--1"></div>
              {performanceMode === 'high' && (
                <>
                  <div className="page-transition__energy-ripple page-transition__energy-ripple--2"></div>
                  <div className="page-transition__energy-ripple page-transition__energy-ripple--3"></div>
                </>
              )}
              
              {/* Plasma Burst */}
              <div className="page-transition__plasma-burst"></div>
              
              {/* Reality Glitch Effect */}
              <div className="page-transition__reality-glitch"></div>
              
              {/* Debris Explosion - Reduced for performance */}
              {Array.from({ length: performanceMode === 'high' ? 8 : 4 }).map((_, i) => (
                <div key={i} className={`page-transition__debris page-transition__debris--${i + 1}`}></div>
              ))}
              
              {/* Screen Crack Effect */}
              <div className="page-transition__screen-crack"></div>
            </>
          )}
          
          {/* Smooth Fade-Out Overlay */}
          <div className="page-transition__fade-out"></div>
        </div>
      )}

      {/* Worker-Based Transition Particles - Enhanced transition effects (MOVED OUTSIDE) */}
      {transitionActive && isFeatureEnabled('particles') && (
        <WorkerParticleCanvas
          className="worker-particle-canvas page-transition__explosion-particles"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            zIndex: 9998, // Just below the main transition overlay (9999)
            opacity: 1, // Start visible since transitionActive controls rendering
            contain: 'strict'
          }}
          particleCount={performanceMode === 'high' ? 35 : performanceMode === 'medium' ? 20 : 10} // Increased for transition effect
          colors={['#ff0040', '#00ff80', '#8000ff', '#ff4000']} // Transition explosion colors
          performanceMode={performanceMode}
          enableGlow={true}
          enableTrails={true}
          isVisible={true} // Always visible when rendered
          interactive={false}
          onPerformanceChange={(mode, metrics) => {
            console.log(`[Transition] Particle performance mode: ${mode}`, metrics);
          }}
        />
      )}

      {/* 3D Depth Container - Performance optimized */}
      {isFeatureEnabled('3d') && (
        <div className="cyber-landing__depth-container gpu-optimized contain-layout-style">
          {/* Far Background Depth Plane */}
          {performanceMode !== 'low' && (
            <div className="cyber-landing__depth-plane--far contain-paint">
              <div 
                className="cyber-landing__layer cyber-landing__layer--1 gpu-accelerated"
                style={{
                  '--mouse-x': `${mousePosition.x}%`,
                  '--mouse-y': `${mousePosition.y}%`,
                } as React.CSSProperties}
              />
            </div>
          )}

          {/* Mid Background Depth Plane */}
          <div className="cyber-landing__depth-plane--mid contain-layout-style">
            <div className="cyber-landing__layer cyber-landing__layer--3 gpu-accelerated" />
            
            {/* Enhanced Circuit Board Pattern - Performance aware */}
            {performanceMode !== 'low' && (
              <div className="cyber-landing__circuit-board gpu-accelerated">
                {/* Horizontal circuit lines */}
                <div 
                  className="cyber-landing__circuit-line cyber-landing__circuit-line--h1 gpu-accelerated"
                  style={{ 
                    transform: `translateZ(-40px) rotateX(2deg)`,
                    animationDuration: `${config.animationDuration * 12}s`, // Much slower
                    opacity: 0.6 // Reduced opacity
                  } as React.CSSProperties}
                ></div>
                <div 
                  className="cyber-landing__circuit-line cyber-landing__circuit-line--h2 gpu-accelerated"
                  style={{ 
                    transform: `translateZ(-20px) rotateX(1deg)`,
                    animationDuration: `${config.animationDuration * 12}s`,
                    opacity: 0.6
                  } as React.CSSProperties}
                ></div>
                
                {/* Vertical circuit lines */}
                <div 
                  className="cyber-landing__circuit-line cyber-landing__circuit-line--v1 gpu-accelerated"
                  style={{ 
                    transform: `translateZ(-30px) rotateY(2deg)`,
                    animationDuration: `${config.animationDuration * 12}s`,
                    opacity: 0.6
                  } as React.CSSProperties}
                ></div>
                <div 
                  className="cyber-landing__circuit-line cyber-landing__circuit-line--v2 gpu-accelerated"
                  style={{ 
                    transform: `translateZ(-10px) rotateY(1deg)`,
                    animationDuration: `${config.animationDuration * 12}s`,
                    opacity: 0.6
                  } as React.CSSProperties}
                ></div>
                
                {/* Circuit nodes - reduced glow */}
                <div 
                  className="cyber-landing__circuit-node cyber-landing__circuit-node--1 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(-35px) scale(0.8)', 
                    filter: isFeatureEnabled('blur') ? 'blur(1px)' : 'none',
                    opacity: 0.5 // Reduced opacity
                  } as React.CSSProperties}
                ></div>
                <div 
                  className="cyber-landing__circuit-node cyber-landing__circuit-node--2 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(-15px) scale(0.9)',
                    opacity: 0.5
                  } as React.CSSProperties}
                ></div>
              </div>
            )}
            
            {/* Enhanced Plasma Energy Streams - Reduced intensity */}
            {performanceMode === 'high' && (
              <div className="cyber-landing__plasma-streams gpu-accelerated">
                <div 
                  className="cyber-landing__plasma-stream cyber-landing__plasma-stream--1 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(-60px) rotateX(3deg) rotateZ(10deg) scale(0.9)',
                    animationDuration: `${config.animationDuration * 10}s`, // Much slower
                    opacity: 0.3 // Reduced opacity
                  } as React.CSSProperties}
                ></div>
                <div 
                  className="cyber-landing__plasma-stream cyber-landing__plasma-stream--2 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(-30px) rotateX(1deg) rotateZ(-8deg) scale(0.95)',
                    animationDuration: `${config.animationDuration * 10}s`,
                    opacity: 0.3
                  } as React.CSSProperties}
                ></div>
              </div>
            )}

            {/* Enhanced Circuit Board - Front Elements with reduced effects */}
            {performanceMode !== 'low' && (
              <div className="cyber-landing__circuit-board gpu-accelerated">
                <div 
                  className="cyber-landing__circuit-line cyber-landing__circuit-line--h3 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(10px) rotateX(-1deg)',
                    animationDuration: `${config.animationDuration * 12}s`,
                    opacity: 0.4
                  } as React.CSSProperties}
                ></div>
                <div 
                  className="cyber-landing__circuit-line cyber-landing__circuit-line--h4 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(30px) rotateX(-2deg)',
                    animationDuration: `${config.animationDuration * 12}s`,
                    opacity: 0.4
                  } as React.CSSProperties}
                ></div>
                
                <div 
                  className="cyber-landing__circuit-line cyber-landing__circuit-line--v3 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(20px) rotateY(-1deg)',
                    animationDuration: `${config.animationDuration * 12}s`,
                    opacity: 0.4
                  } as React.CSSProperties}
                ></div>
                <div 
                  className="cyber-landing__circuit-line cyber-landing__circuit-line--v4 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(40px) rotateY(-2deg)',
                    animationDuration: `${config.animationDuration * 12}s`,
                    opacity: 0.4
                  } as React.CSSProperties}
                ></div>
                
                <div 
                  className="cyber-landing__circuit-node cyber-landing__circuit-node--3 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(15px) scale(1.05)',
                    opacity: 0.3
                  } as React.CSSProperties}
                ></div>
                <div 
                  className="cyber-landing__circuit-node cyber-landing__circuit-node--4 gpu-accelerated"
                  style={{ 
                    transform: 'translateZ(35px) scale(1.1)',
                    opacity: 0.3
                  } as React.CSSProperties}
                ></div>
              </div>
            )}

            {/* Focus Depth Plane */}
            <div className="cyber-landing__depth-plane--focus contain-paint">
              {/* Data Streams with reduced animation speed */}
              {dataPackets.length > 0 && (
                <div className="cyber-landing__data-streams gpu-accelerated">
                  {dataPackets.map((packet) => (
                    <div 
                      key={packet.id}
                      className={`
                        cyber-landing__data-packet cyber-landing__data-packet--${packet.id + 1}
                        gpu-accelerated
                      `}
                      style={{
                        animationDuration: `${config.animationDuration * 6}s`, // Slower
                        opacity: 0.5 // Reduced opacity
                      }}
                    ></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Worker-Based Particle System - Replaces old canvas implementation */}
      {isFeatureEnabled('particles') && (
        <WorkerParticleCanvas
          className="cyber-landing__particle-canvas gpu-accelerated"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            zIndex: -50,
            opacity: performanceMode === 'high' ? 0.4 : 0.2, // Reduced opacity to match original
            contain: 'strict'
          }}
          particleCount={particleCount}
          colors={particleColors}
          performanceMode={performanceMode}
          enableGlow={isFeatureEnabled('glow') && performanceMode === 'high'}
          enableTrails={true}
          isVisible={isVisible}
          onPerformanceChange={handleParticlePerformanceChange}
        />
      )}

      {/* Floating Geometric Elements - Performance aware */}
      {isFeatureEnabled('3d') && (
        <div className="cyber-landing__geometry gpu-accelerated">
          {Array.from({ length: performanceMode === 'high' ? 2 : 1 }).map((_, i) => ( // Reduced count
            <div
              key={i}
              className={`
                cyber-landing__shape cyber-landing__shape--${i}
                float-element gpu-accelerated
              `}
              style={{ 
                animationDelay: `${i * 3}s`, // Longer delay
                animationDuration: `${config.animationDuration * 8}s`, // Slower
                transform: `translateZ(${i * 40}px) rotateX(${i * 45}deg) rotateY(${i * 30}deg) scale(${0.8 + (i * 0.2)})`,
                opacity: 0.3 + (i * 0.1), // Reduced opacity
                contain: 'layout style'
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}

      {/* Title Portal - Always visible but performance optimized */}
      <div className="cyber-landing__header contain-layout-style responsive-container">
        <div className="cyber-landing__title-portal gpu-optimized mobile-focus-visible" onClick={handleTitleClick}>
          <div className="cyber-landing__title-portal-container">
            
            {/* Orbital System with reduced glow */}
            <div className="cyber-landing__orbital-system gpu-accelerated">
              <div className="cyber-landing__orbit-ring cyber-landing__orbit-ring--1">
                <div className="cyber-landing__orbit-dot cyber-landing__orbit-dot--1"></div>
              </div>
              <div className="cyber-landing__orbit-ring cyber-landing__orbit-ring--2">
                <div className="cyber-landing__orbit-dot cyber-landing__orbit-dot--2"></div>
              </div>
              {performanceMode === 'high' && (
                <div className="cyber-landing__orbit-ring cyber-landing__orbit-ring--3">
                  <div className="cyber-landing__orbit-dot cyber-landing__orbit-dot--3"></div>
                </div>
              )}
            </div>

            {/* Floating Navigation Cards with reduced glow */}
            <div className="cyber-landing__nav-cards gpu-accelerated">
              {navCards.map((card, index) => (
                <div
                  key={card.id}
                  className={`
                    cyber-landing__nav-card nav-card-optimized
                    gpu-optimized touch-target-comfortable mobile-focus-visible
                    pointer-coarse:touch-target
                  `}
                  style={{
                    '--card-angle': `${card.angle}deg`,
                    '--card-delay': `${index * 0.3}s`, // Slower cascade
                    animationDuration: `${config.animationDuration * 3}s`, // Slower
                    contain: 'layout style',
                    willChange: 'transform'
                  } as React.CSSProperties}
                  onClick={() => handleNavigation(card.href)}
                >
                  <div className="cyber-landing__nav-card-inner">
                    <div className="cyber-landing__nav-card-label matrix-text">{card.label}</div>
                    {/* Removed glow effect for cleaner look */}
                  </div>
                </div>
              ))}
            </div>

            {/* Central Title with subtle effects */}
            <div className="cyber-landing__title-core gpu-optimized">
              <div 
                className="cyber-landing__title-main-portal matrix-text"
                data-text="GRIFFIN'S"
                style={{
                  animationDuration: `${config.animationDuration * 3}s`, // Slower
                  textShadow: '0 0 8px currentColor', // Reduced glow
                  contain: 'layout style'
                }}
              >
                GRIFFIN'S
              </div>
              <div 
                className="cyber-landing__title-sub-portal matrix-text"
                style={{
                  animationDuration: `${config.animationDuration * 4}s` // Slower
                }}
              >
                DIGITAL REALM
              </div>
              
              {/* Energy Core with much subtler effect */}
              {isFeatureEnabled('glow') && performanceMode === 'high' && (
                <div className="cyber-landing__energy-core gpu-accelerated">
                  <div 
                    className="cyber-landing__energy-pulse cyber-landing__energy-pulse--1 energy-core"
                    style={{ 
                      animationDuration: `${config.animationDuration * 5}s`, // Much slower
                      opacity: 0.4 // Reduced opacity
                    }}
                  ></div>
                  <div 
                    className="cyber-landing__energy-pulse cyber-landing__energy-pulse--2 energy-core-inner"
                    style={{ 
                      animationDuration: `${config.animationDuration * 4}s`,
                      opacity: 0.3
                    }}
                  ></div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Bottom Terminal - Optimized */}
      <div className={`
        cyber-landing__terminal-wrapper cyber-landing__terminal-wrapper--bottom
        terminal-optimized gpu-optimized mobile-optimized responsive-container
        ${terminalCollapsed ? 'cyber-landing__terminal-wrapper--collapsed' : ''}
      `}>
        <div className="cyber-landing__terminal-toggle touch-target-comfortable mobile-focus-visible" onClick={toggleTerminal}>
          <span className="cyber-landing__terminal-toggle-icon">
            {terminalCollapsed ? '▲' : '▼'}
          </span>
          <span className="cyber-landing__terminal-toggle-text matrix-text">
            {terminalCollapsed ? 'EXPAND TERMINAL' : 'COLLAPSE TERMINAL'}
          </span>
        </div>
        <div className="cyber-landing__terminal-content">
          <Terminal onNavigate={handleNavigation} className="cyber-landing__terminal" />
        </div>
      </div>

      {/* Scan Lines - Much more subtle */}
      <div 
        className="cyber-landing__scanlines scanlines-optimized"
        style={{
          animationDuration: `${config.animationDuration * 4}s`, // Much slower
          opacity: performanceMode === 'low' ? 0.05 : 0.1 // Much more subtle
        }}
      ></div>

      {/* Terminal Summon Button with reduced glow */}
      {showSummonButton && terminalCollapsed && !isTransitioning && (
        <button 
          className={`
            cyber-landing__summon-button nav-card-optimized
            gpu-optimized animate-float-gentle touch-target-large mobile-focus-visible
          `}
          onClick={toggleTerminal}
          aria-label="Summon Terminal"
          style={{
            animationDuration: `${config.animationDuration * 6}s`, // Slower
            contain: 'layout style'
          }}
        >
          <div className="cyber-landing__summon-icon">
            <span className="cyber-landing__summon-text matrix-text">TERMINAL</span>
            <span className="cyber-landing__summon-symbol">&gt;_</span>
          </div>
          {/* Removed glow effect */}
        </button>
      )}

      {/* Performance debug info - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-4 left-4 bg-black/80 text-green-400 p-2 text-xs font-mono border border-green-400/30 rounded">
          Performance: {performanceMode} | FPS: {metrics.fps} | Memory: {metrics.memoryUsage}MB
        </div>
      )}
    </main>
  );
};

export default MatrixLanding; 